import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faChartLine,
  faPlus,
  faTimes,
  faEdit,
  faTrash,
  faSync,
  faDollarSign,
  faArrowUp,
  faArrowDown,
  faSave,
  faWindowClose,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { portfolioApi } from '../api/portfolioApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const PortfolioPage = () => {
  const { maskAmounts } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [portfolioValue, setPortfolioValue] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingValue, setLoadingValue] = useState(false);
  const [error, setError] = useState(null);
  
  // Portfolio form
  const [showPortfolioForm, setShowPortfolioForm] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [portfolioName, setPortfolioName] = useState('');
  const [portfolioDescription, setPortfolioDescription] = useState('');
  const [portfolioCash, setPortfolioCash] = useState('');
  
  // Stock form
  const [showStockForm, setShowStockForm] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockSymbol, setStockSymbol] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  const [stockBuyPrice, setStockBuyPrice] = useState('');
  const [stockBuyDate, setStockBuyDate] = useState('');
  const [stockNotes, setStockNotes] = useState('');
  
  // Chart timeframe
  const [chartTimeframe, setChartTimeframe] = useState('30'); // days
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadPortfolios();
  }, []);

  useEffect(() => {
    if (selectedPortfolio) {
      loadPortfolioValue();
      loadSnapshots();
    }
  }, [selectedPortfolio, chartTimeframe]);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portfolioApi.getPortfolios();
      setPortfolios(response.data || []);
      if (response.data && response.data.length > 0 && !selectedPortfolio) {
        setSelectedPortfolio(response.data[0]);
      }
    } catch (err) {
      console.error('Error loading portfolios:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolioValue = async () => {
    if (!selectedPortfolio) return;
    
    try {
      setLoadingValue(true);
      const response = await portfolioApi.getPortfolioValue(selectedPortfolio.id);
      setPortfolioValue(response.data);
    } catch (err) {
      console.error('Error loading portfolio value:', err);
    } finally {
      setLoadingValue(false);
    }
  };

  const loadSnapshots = async () => {
    if (!selectedPortfolio) return;
    
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(chartTimeframe));
      const startDateStr = startDate.toISOString().split('T')[0];
      
      const response = await portfolioApi.getSnapshots(selectedPortfolio.id, startDateStr, endDate);
      setSnapshots(response.data || []);
    } catch (err) {
      console.error('Error loading snapshots:', err);
    }
  };

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    if (!portfolioName.trim()) {
      setError('Portfolio name is required');
      return;
    }

    try {
      setError(null);
      const response = await portfolioApi.createPortfolio(
        portfolioName,
        portfolioDescription || null,
        portfolioCash ? parseFloat(portfolioCash) : 0
      );
      await loadPortfolios();
      setSelectedPortfolio(response.data);
      setShowPortfolioForm(false);
      setPortfolioName('');
      setPortfolioDescription('');
      setPortfolioCash('');
    } catch (err) {
      console.error('Error creating portfolio:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create portfolio');
    }
  };

  const handleUpdatePortfolio = async (e) => {
    e.preventDefault();
    if (!portfolioName.trim()) {
      setError('Portfolio name is required');
      return;
    }

    try {
      setError(null);
      await portfolioApi.updatePortfolio(
        editingPortfolio.id,
        portfolioName,
        portfolioDescription || null,
        portfolioCash !== '' ? parseFloat(portfolioCash) : null
      );
      await loadPortfolios();
      setShowPortfolioForm(false);
      setEditingPortfolio(null);
      setPortfolioName('');
      setPortfolioDescription('');
      setPortfolioCash('');
    } catch (err) {
      console.error('Error updating portfolio:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update portfolio');
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm('Are you sure you want to delete this portfolio? This will also delete all stocks and snapshots.')) {
      return;
    }

    try {
      setError(null);
      await portfolioApi.deletePortfolio(id);
      await loadPortfolios();
      if (selectedPortfolio && selectedPortfolio.id === id) {
        setSelectedPortfolio(null);
        setPortfolioValue(null);
        setSnapshots([]);
      }
    } catch (err) {
      console.error('Error deleting portfolio:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete portfolio');
    }
  };

  const handleEditPortfolio = (portfolio) => {
    setEditingPortfolio(portfolio);
    setPortfolioName(portfolio.name);
    setPortfolioDescription(portfolio.description || '');
    setPortfolioCash(portfolio.cash ? portfolio.cash.toString() : '');
    setShowPortfolioForm(true);
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (!stockSymbol || !stockQuantity || !stockBuyPrice) {
      setError('Symbol, quantity, and buy price are required');
      return;
    }

    try {
      setError(null);
      await portfolioApi.addStock(
        selectedPortfolio.id,
        stockSymbol.toUpperCase(),
        parseFloat(stockQuantity),
        parseFloat(stockBuyPrice),
        stockBuyDate || null,
        stockNotes || null
      );
      await loadPortfolioValue();
      await createSnapshot();
      setShowStockForm(false);
      setStockSymbol('');
      setStockQuantity('');
      setStockBuyPrice('');
      setStockBuyDate('');
      setStockNotes('');
    } catch (err) {
      console.error('Error adding stock:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add stock');
    }
  };

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    if (!stockSymbol || !stockQuantity || !stockBuyPrice) {
      setError('Symbol, quantity, and buy price are required');
      return;
    }

    try {
      setError(null);
      await portfolioApi.updateStock(
        selectedPortfolio.id,
        editingStock.id,
        stockSymbol.toUpperCase(),
        parseFloat(stockQuantity),
        parseFloat(stockBuyPrice),
        stockBuyDate || null,
        stockNotes || null
      );
      await loadPortfolioValue();
      await createSnapshot();
      setShowStockForm(false);
      setEditingStock(null);
      setStockSymbol('');
      setStockQuantity('');
      setStockBuyPrice('');
      setStockBuyDate('');
      setStockNotes('');
    } catch (err) {
      console.error('Error updating stock:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update stock');
    }
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Are you sure you want to remove this stock from the portfolio?')) {
      return;
    }

    try {
      setError(null);
      await portfolioApi.removeStock(selectedPortfolio.id, stockId);
      await loadPortfolioValue();
      await createSnapshot();
    } catch (err) {
      console.error('Error deleting stock:', err);
      setError(err.response?.data?.error || err.message || 'Failed to remove stock');
    }
  };

  const handleEditStock = (stock) => {
    setEditingStock(stock);
    setStockSymbol(stock.symbol);
    setStockQuantity(stock.quantity.toString());
    setStockBuyPrice(stock.buy_price.toString());
    setStockBuyDate(stock.buy_date || '');
    setStockNotes(stock.notes || '');
    setShowStockForm(true);
  };

  const createSnapshot = async () => {
    if (!selectedPortfolio) return;
    
    try {
      await portfolioApi.createSnapshot(selectedPortfolio.id);
      await loadSnapshots();
    } catch (err) {
      console.error('Error creating snapshot:', err);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    if (maskAmounts) return '***';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faSort} className="text-gray-400 text-[8px] ml-1" />;
    }
    return sortDirection === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="text-indigo-600 text-[8px] ml-1" />
      : <FontAwesomeIcon icon={faSortDown} className="text-indigo-600 text-[8px] ml-1" />;
  };

  const sortedStocks = portfolioValue?.stocks ? [...portfolioValue.stocks].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aVal, bVal;
    switch (sortColumn) {
      case 'symbol':
        aVal = a.symbol?.toUpperCase() || '';
        bVal = b.symbol?.toUpperCase() || '';
        break;
      case 'quantity':
        aVal = parseFloat(a.quantity) || 0;
        bVal = parseFloat(b.quantity) || 0;
        break;
      case 'buyPrice':
        aVal = parseFloat(a.buy_price) || 0;
        bVal = parseFloat(b.buy_price) || 0;
        break;
      case 'currentPrice':
        aVal = parseFloat(a.currentPrice) || 0;
        bVal = parseFloat(b.currentPrice) || 0;
        break;
      case 'cost':
        aVal = parseFloat(a.cost) || 0;
        bVal = parseFloat(b.cost) || 0;
        break;
      case 'value':
        aVal = parseFloat(a.value) || 0;
        bVal = parseFloat(b.value) || 0;
        break;
      case 'gainLoss':
        aVal = parseFloat(a.gainLoss) || 0;
        bVal = parseFloat(b.gainLoss) || 0;
        break;
      default:
        return 0;
    }
    
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  }) : [];

  // Prepare chart data
  const chartData = snapshots.map(snapshot => ({
    date: snapshot.snapshot_date,
    value: parseFloat(snapshot.total_value),
    cost: parseFloat(snapshot.total_cost),
    gainLoss: parseFloat(snapshot.total_gain_loss),
  }));

  // Compute business-day delta for current portfolio:
  // current live value (based on latest prices) minus previous trading day's snapshot
  let previousBusinessSnapshot = null;
  if (snapshots && snapshots.length > 0) {
    const sorted = [...snapshots].sort(
      (a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date)
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      const d = new Date(sorted[i].snapshot_date);
      d.setHours(0, 0, 0, 0);
      const dow = d.getDay(); // 0 = Sunday, 6 = Saturday

      // Skip weekends and any snapshots from today or the future
      if (dow === 0 || dow === 6 || d >= today) continue;

      previousBusinessSnapshot = sorted[i];
      break;
    }
  }

  const portfolioDeltaValue =
    portfolioValue && previousBusinessSnapshot
      ? parseFloat(portfolioValue.totalValue) - parseFloat(previousBusinessSnapshot.total_value)
      : null;

  const portfolioDeltaPercent =
    portfolioValue &&
    previousBusinessSnapshot &&
    previousBusinessSnapshot.total_value
      ? (portfolioDeltaValue / parseFloat(previousBusinessSnapshot.total_value)) * 100
      : null;

  return (
    <main className="flex-1 container mx-auto px-4 lg:px-6 py-3">
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartLine} className="mr-2 text-indigo-600" />
          Portfolio Tracker
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Track your stocks from other trading companies and monitor portfolio performance
        </p>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left Column - Portfolio List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-800">Portfolios</h2>
              <button
                onClick={() => {
                  setEditingPortfolio(null);
                  setPortfolioName('');
                  setPortfolioDescription('');
                  setPortfolioCash('');
                  setShowPortfolioForm(true);
                }}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-xs font-medium flex items-center space-x-1"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                <span>New</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4 text-gray-500">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-lg mb-2" />
                <p className="text-xs">Loading portfolios...</p>
              </div>
            ) : portfolios.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                <p>No portfolios yet. Create one to get started!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {portfolios.map((portfolio) => (
                  <div
                    key={portfolio.id}
                    onClick={() => setSelectedPortfolio(portfolio)}
                    className={`p-2 rounded-md cursor-pointer transition-colors ${
                      selectedPortfolio?.id === portfolio.id
                        ? 'bg-indigo-100 border-2 border-indigo-500'
                        : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-800">{portfolio.name}</div>
                        {portfolio.description && (
                          <div className="text-xs text-gray-600 mt-0.5">{portfolio.description}</div>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditPortfolio(portfolio);
                          }}
                          className="p-1 text-gray-600 hover:text-indigo-600 transition-colors"
                        >
                          <FontAwesomeIcon icon={faEdit} className="text-xs" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePortfolio(portfolio.id);
                          }}
                          className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Portfolio Details */}
        <div className="lg:col-span-2 space-y-3">
          {selectedPortfolio ? (
            <>
              {/* Portfolio Value Summary */}
              <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-gray-800">{selectedPortfolio.name}</h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={loadPortfolioValue}
                      disabled={loadingValue}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center space-x-1"
                    >
                      <FontAwesomeIcon icon={loadingValue ? faSpinner : faSync} className={loadingValue ? 'animate-spin' : ''} />
                      <span>Refresh</span>
                    </button>
                    <button
                      onClick={createSnapshot}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium flex items-center space-x-1"
                    >
                      <FontAwesomeIcon icon={faSave} className="text-xs" />
                      <span>Save Snapshot</span>
                    </button>
                  </div>
                </div>

                {portfolioValue ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <div className="text-xs text-gray-600 mb-1">Total Cost</div>
                      <div className="text-base font-bold text-gray-800">
                        {formatCurrency(portfolioValue.totalCost)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <div className="text-xs text-gray-600 mb-1">Current Value</div>
                      <div className="text-base font-bold text-gray-800 flex flex-col">
                        <span>{formatCurrency(portfolioValue.totalValue)}</span>
                        {portfolioDeltaValue !== null && (
                          <span
                            className={`text-[11px] font-semibold ${
                              portfolioDeltaValue >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {portfolioDeltaValue >= 0 ? '+' : ''}
                            {formatCurrency(portfolioDeltaValue)}
                            {portfolioDeltaPercent !== null &&
                              !Number.isNaN(portfolioDeltaPercent) && (
                                <>
                                  {' '}
                                  (
                                  {portfolioDeltaPercent >= 0 ? '+' : ''}
                                  {portfolioDeltaPercent.toFixed(2)}%)
                                </>
                              )}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <div className="text-xs text-gray-600 mb-1">Gain/Loss</div>
                      <div
                        className={`text-base font-bold ${
                          portfolioValue.totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(portfolioValue.totalGainLoss)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <div className="text-xs text-gray-600 mb-1">Gain/Loss %</div>
                      <div
                        className={`text-base font-bold ${
                          portfolioValue.totalGainLossPercent >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {formatPercent(portfolioValue.totalGainLossPercent)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Click "Refresh" to calculate portfolio value
                  </div>
                )}
              </div>

              {/* Portfolio Value Chart */}
              {snapshots.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-gray-800">Portfolio Value History</h2>
                    <select
                      value={chartTimeframe}
                      onChange={(e) => setChartTimeframe(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="7">7 Days</option>
                      <option value="30">30 Days</option>
                      <option value="90">90 Days</option>
                      <option value="180">180 Days</option>
                      <option value="365">1 Year</option>
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '8px',
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="Portfolio Value"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#9ca3af"
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        name="Total Cost"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Stocks Table */}
              <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-gray-800">Stocks</h2>
                  <button
                    onClick={() => {
                      setEditingStock(null);
                      setStockSymbol('');
                      setStockQuantity('');
                      setStockBuyPrice('');
                      setStockBuyDate('');
                      setStockNotes('');
                      setShowStockForm(true);
                    }}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium flex items-center space-x-1"
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-xs" />
                    <span>Add Stock</span>
                  </button>
                </div>

                {portfolioValue && portfolioValue.stocks && portfolioValue.stocks.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('symbol')}
                          >
                            <div className="flex items-center">
                              Symbol
                              {getSortIcon('symbol')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('quantity')}
                          >
                            <div className="flex items-center">
                              Quantity
                              {getSortIcon('quantity')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('buyPrice')}
                          >
                            <div className="flex items-center">
                              Buy Price
                              {getSortIcon('buyPrice')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('currentPrice')}
                          >
                            <div className="flex items-center">
                              Current Price
                              {getSortIcon('currentPrice')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('cost')}
                          >
                            <div className="flex items-center">
                              Cost
                              {getSortIcon('cost')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('value')}
                          >
                            <div className="flex items-center">
                              Value
                              {getSortIcon('value')}
                            </div>
                          </th>
                          <th 
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('gainLoss')}
                          >
                            <div className="flex items-center">
                              Gain/Loss
                              {getSortIcon('gainLoss')}
                            </div>
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedStocks.map((stock) => (
                          <tr key={stock.id}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{stock.symbol}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{parseFloat(stock.quantity).toLocaleString()}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(stock.buy_price)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(stock.currentPrice)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(stock.cost)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(stock.value)}</td>
                            <td className={`px-4 py-2 text-sm font-medium ${stock.gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(stock.gainLoss)} ({formatPercent(stock.gainLossPercent)})
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditStock(stock)}
                                  className="text-indigo-600 hover:text-indigo-800"
                                >
                                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                                </button>
                                <button
                                  onClick={() => handleDeleteStock(stock.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No stocks in this portfolio. Add stocks to track your positions.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 border border-gray-100 text-center">
              <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-400 mb-3" />
              <p className="text-gray-600">Select a portfolio to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Portfolio Form Modal */}
      {showPortfolioForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingPortfolio ? 'Edit Portfolio' : 'New Portfolio'}
              </h2>
              <button
                onClick={() => {
                  setShowPortfolioForm(false);
                  setEditingPortfolio(null);
                  setPortfolioName('');
                  setPortfolioDescription('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={editingPortfolio ? handleUpdatePortfolio : handleCreatePortfolio}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={portfolioName}
                  onChange={(e) => setPortfolioName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={portfolioDescription}
                  onChange={(e) => setPortfolioDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cash Balance</label>
                <input
                  type="number"
                  step="0.01"
                  value={portfolioCash}
                  onChange={(e) => setPortfolioCash(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPortfolioForm(false);
                    setEditingPortfolio(null);
                    setPortfolioName('');
                    setPortfolioDescription('');
                    setPortfolioCash('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  {editingPortfolio ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock Form Modal */}
      {showStockForm && selectedPortfolio && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingStock ? 'Edit Stock' : 'Add Stock'}
              </h2>
              <button
                onClick={() => {
                  setShowStockForm(false);
                  setEditingStock(null);
                  setStockSymbol('');
                  setStockQuantity('');
                  setStockBuyPrice('');
                  setStockBuyDate('');
                  setStockNotes('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={editingStock ? handleUpdateStock : handleAddStock}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol *</label>
                <input
                  type="text"
                  value={stockSymbol}
                  onChange={(e) => setStockSymbol(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                <input
                  type="number"
                  step="0.000001"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Buy Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={stockBuyPrice}
                  onChange={(e) => setStockBuyPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Buy Date</label>
                <input
                  type="date"
                  value={stockBuyDate}
                  onChange={(e) => setStockBuyDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={stockNotes}
                  onChange={(e) => setStockNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowStockForm(false);
                    setEditingStock(null);
                    setStockSymbol('');
                    setStockQuantity('');
                    setStockBuyPrice('');
                    setStockBuyDate('');
                    setStockNotes('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  {editingStock ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default PortfolioPage;

